import { useState } from "react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/shared/image-upload";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUploadImage } from "@/hooks/use-files";
import { authClient, useSession } from "@/lib/auth-client";

/**
 * Reference usage of the upload feature: upload an image through the server
 * pipeline, then point the Better Auth `user.image` at the resulting file id.
 * `UserAvatar` resolves that id to a presigned URL wherever the avatar renders.
 */
export function ProfileAvatarCard() {
  const { data: session, refetch } = useSession();
  const uploadImage = useUploadImage();
  const [isSaving, setIsSaving] = useState(false);

  const user = session?.user;
  if (!user) return null;

  async function handleSelect(file: File) {
    setIsSaving(true);
    try {
      const record = await uploadImage.mutateAsync(file);
      await authClient.updateUser({ image: record.id });
      refetch();
    } catch {
      // The upload hook already surfaced a toast; nothing to add here.
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemove() {
    setIsSaving(true);
    try {
      await authClient.updateUser({ image: "" });
      refetch();
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    } finally {
      setIsSaving(false);
    }
  }

  const busy = isSaving || uploadImage.isPending;

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle>Profile photo</CardTitle>
        <CardDescription>
          Upload a photo — it's resized and converted to WebP before storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <UserAvatar user={user} className="h-20 w-20 text-lg" />
        <div className="flex-1 space-y-3">
          <ImageUpload onSelect={handleSelect} isUploading={busy} />
          {user.image ? (
            <Button variant="ghost" size="sm" onClick={handleRemove} disabled={busy}>
              Remove photo
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
